import React from 'react';

// Public privacy policy. Reachable at /privacy (no auth). Content is a starting
// point — replace the [bracketed] legal details with your registered entity's.
const EFFECTIVE_DATE = 'July 23, 2026';
const CONTACT_EMAIL = 'privacy@hrtrackings.com';

const H = ({ children }) => <h2 style={{ fontSize: '1.15rem', fontWeight: 700, margin: '28px 0 8px', color: 'var(--text)' }}>{children}</h2>;
const P = ({ children }) => <p style={{ color: 'var(--muted)', lineHeight: 1.7, margin: '0 0 12px' }}>{children}</p>;

export default function PrivacyPolicy({ goToLogin }) {
  return (
    <div style={{ minHeight: '100vh', background: 'transparent', padding: '32px 16px', color: 'var(--text)' }}>
      <div style={{ maxWidth: 760, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <div style={{ fontSize: '1.6rem', fontWeight: 800 }}>HR<span style={{ color: 'var(--primary-strong, #4f46e5)' }}>Track</span></div>
          {goToLogin && (
            <button onClick={goToLogin} style={{ background: 'none', border: 'none', color: '#4f46e5', cursor: 'pointer', fontSize: '0.9rem' }}>← Back to sign in</button>
          )}
        </div>

        <div className="card" style={{ padding: '32px 28px' }}>
          <h1 style={{ fontSize: '1.7rem', fontWeight: 800, marginBottom: 6 }}>Privacy Policy</h1>
          <P>Effective date: {EFFECTIVE_DATE}</P>

          <H>1. Who we are</H>
          <P>
            HRTrack (“we”, “us”) provides employee time-tracking and workforce-monitoring software. This policy explains how we
            handle personal data. For the workforce data our customers collect through HRTrack, the customer (your employer) is the
            <strong> data controller</strong> and HRTrack acts as a <strong>data processor</strong> on their behalf. For account and
            billing information about the customer, HRTrack is the controller.
          </P>

          <H>2. Data we process</H>
          <P>
            • <strong>Account data:</strong> name, work email, username, hashed password, workspace and role.<br />
            • <strong>Workforce data (on behalf of the employer):</strong> clock in/out times, worked hours, breaks, leave requests,
            screenshots, active application and window titles, and keyboard/mouse activity counts captured by the desktop agent.<br />
            • <strong>Billing data:</strong> plan, subscription status and customer identifiers (payment card details are handled by
            our payment processor, not stored by us).<br />
            • <strong>Technical data:</strong> IP address, device/browser information and security logs.
          </P>

          <H>3. How we use data</H>
          <P>
            To provide and secure the service, authenticate users, deliver time-tracking and reporting features to employers, process
            billing, provide support, and meet legal obligations. We do not sell personal data or use it for advertising.
          </P>

          <H>4. Legal basis</H>
          <P>
            We process data to perform our contract with the customer, to pursue legitimate interests in operating and securing the
            service, and to comply with legal obligations. Employers are responsible for establishing a lawful basis and providing
            required notices for the workforce monitoring they configure.
          </P>

          <H>5. Sharing &amp; subprocessors</H>
          <P>
            We share data with vetted subprocessors that help us run the service — cloud hosting, email delivery and payment
            processing — under contractual data-protection terms. We may disclose data where required by law.
          </P>

          <H>6. Retention</H>
          <P>
            We retain workforce data for as long as the employer’s workspace is active, and delete or anonymize it after account
            closure subject to legal retention requirements. Encrypted database backups are kept on a rolling basis and then purged.
          </P>

          <H>7. Security</H>
          <P>
            Data is encrypted in transit (HTTPS/TLS). Passwords are stored using industry-standard hashing, optional two-factor
            authentication is available, and access is restricted and logged. No method of transmission or storage is 100% secure,
            but we work to protect your information.
          </P>

          <H>8. Your rights</H>
          <P>
            Depending on your location, you may have rights to access, correct, export, or delete your personal data, and to object
            to or restrict certain processing. Signed-in users can export their own data from <em>Profile → Your Data</em>, and
            workspace admins can export or request deletion from <em>Settings → Privacy &amp; Data</em>. For workforce data, direct
            requests to your employer as the controller; we will assist them in responding.
          </P>

          <H>9. International transfers</H>
          <P>
            Where data is transferred across borders, we rely on appropriate safeguards such as standard contractual clauses.
          </P>

          <H>10. Cookies</H>
          <P>
            We use strictly necessary cookies and local storage to keep you signed in and remember preferences. We do not use
            third-party advertising cookies.
          </P>

          <H>11. Contact</H>
          <P>
            Questions or requests? Contact us at <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: '#4f46e5' }}>{CONTACT_EMAIL}</a>.
            [Insert registered company name, address and, if applicable, EU/UK representative.]
          </P>

          <P>We may update this policy; material changes will be posted here with a revised effective date.</P>
        </div>
      </div>
    </div>
  );
}
